const configuredBase = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
const API = `${configuredBase}/api`;
const ATTRIBUTION_KEY = 'bizsarthi_first_touch_attribution_v1';
const CONSENT_KEY = 'bizsarthi_analytics_consent_v1';
const FORM_STARTED_AT = Date.now();
const GOOGLE_ADS_CONTACT_CONVERSIONS = {
  call: 'AW-18247255508/Zyi8CJyphewcENSL_PxD',
  whatsapp: 'AW-18247255508/uv-OCL7Y7dEcENSL_PxD',
};
const GOOGLE_ADS_LEAD_CONVERSION =
  'AW-18247255508/sS-GCLnlkewcENSL_PxD';
const blockedAnalyticsKeys = new Set([
  'name',
  'email',
  'mobile',
  'phone',
  'message',
  'address',
]);
const attributionFields = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'gbraid',
  'wbraid',
];

export function trackEvent(name, data = {}) {
  try {
    if (window.localStorage.getItem(CONSENT_KEY) !== 'granted') return;
    const safeData = Object.entries(data).reduce((result, [key, value]) => {
      if (!blockedAnalyticsKeys.has(key.toLowerCase())) {
        result[key] = typeof value === 'string' ? value.slice(0, 250) : value;
      }
      return result;
    }, {});
    window.dataLayer = window.dataLayer || [];
    if (typeof window.gtag === 'function') {
      window.gtag('event', name, safeData);
    } else {
      window.dataLayer.push({ event: name, ...safeData });
    }
  } catch {
    // Analytics must never block lead capture.
  }
}

export function trackContactConversion(channel, data = {}) {
  const normalizedChannel = channel === 'call' ? 'call' : 'whatsapp';
  trackEvent(`${normalizedChannel}_click`, data);

  try {
    const isAdsLandingPage = window.location.pathname.startsWith('/ads/');
    const configuredConversionId =
      normalizedChannel === 'call'
        ? process.env.REACT_APP_GOOGLE_ADS_CALL_CONVERSION
        : process.env.REACT_APP_GOOGLE_ADS_WHATSAPP_CONVERSION;
    const conversionId =
      configuredConversionId ||
      (isAdsLandingPage
        ? GOOGLE_ADS_CONTACT_CONVERSIONS[normalizedChannel]
        : '');
    if (conversionId && typeof window.gtag === 'function') {
      window.gtag('event', 'conversion', { send_to: conversionId });
    }
  } catch {
    // Contact actions must never be blocked by analytics.
  }
}

export function trackLeadConversion(data = {}) {
  trackEvent('generate_lead', data);
  const isAdsLandingPage = window.location.pathname.startsWith('/ads/');

  // Standard pages preserve opt-in-only analytics. Ads landing pages load the
  // Google tag in Consent Mode, so the conversion event can be handled using
  // the visitor's consent state (full measurement when granted; a restricted,
  // cookieless ping when denied).
  try {
    if (
      !isAdsLandingPage &&
      window.localStorage.getItem(CONSENT_KEY) !== 'granted'
    ) return;
  } catch {
    if (!isAdsLandingPage) return;
  }
  const sendTo =
    process.env.REACT_APP_GOOGLE_ADS_LEAD_CONVERSION ||
    (isAdsLandingPage ? GOOGLE_ADS_LEAD_CONVERSION : '');
  if (sendTo && typeof window.gtag === 'function') {
    const conversion = {
      send_to: sendTo,
      transaction_id: data.lead_id || '',
    };
    const configuredValue = Number(
      process.env.REACT_APP_GOOGLE_ADS_LEAD_VALUE,
    );
    if (Number.isFinite(configuredValue) && configuredValue > 0) {
      conversion.value = configuredValue;
      conversion.currency = 'INR';
    }
    window.gtag('event', 'conversion', conversion);
  }
}

function readStoredAttribution() {
  try {
    return JSON.parse(window.localStorage.getItem(ATTRIBUTION_KEY) || '{}');
  } catch {
    return {};
  }
}

function attributionFromUrl() {
  const params = new URLSearchParams(window.location.search);
  return attributionFields.reduce((result, key) => {
    const value = params.get(key);
    if (value) result[key] = value.slice(0, 250);
    return result;
  }, {});
}

export function rememberAttribution() {
  try {
    const current = attributionFromUrl();
    const stored = readStoredAttribution();
    const merged = { ...current, ...stored };
    if (Object.keys(merged).length) {
      window.localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(merged));
    }
    return merged;
  } catch {
    return {};
  }
}

export function captureContext() {
  const attribution = rememberAttribution();
  return {
    page_url: window.location.href,
    referrer: document.referrer || '',
    utm_source: attribution.utm_source || '',
    utm_medium: attribution.utm_medium || '',
    utm_campaign: attribution.utm_campaign || '',
    utm_term: attribution.utm_term || '',
    utm_content: attribution.utm_content || '',
    gclid: attribution.gclid || '',
    gbraid: attribution.gbraid || '',
    wbraid: attribution.wbraid || '',
  };
}

function createRequestId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `lead-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

function stableRequestId(payload) {
  if (payload.request_id) return payload.request_id;
  const fingerprint = [
    payload.service || '',
    payload.mobile || '',
    payload.email || '',
    payload.location || '',
  ].join('|').toLowerCase();
  const storageKey = 'bizsarthi_pending_lead_request_v1';
  try {
    const saved = JSON.parse(window.sessionStorage.getItem(storageKey) || '{}');
    if (
      saved.fingerprint === fingerprint &&
      saved.requestId &&
      Date.now() - saved.createdAt < 30 * 60 * 1000
    ) {
      return saved.requestId;
    }
    const requestId = createRequestId();
    window.sessionStorage.setItem(
      storageKey,
      JSON.stringify({ fingerprint, requestId, createdAt: Date.now() }),
    );
    return requestId;
  } catch {
    return createRequestId();
  }
}

export async function submitLead(payload) {
  const response = await fetch(`${API}/leads`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...payload,
      request_id: stableRequestId(payload),
      website:
        document.getElementById('bizsarthi-website-field')?.value?.slice(0, 200) ||
        '',
      form_started_at: FORM_STARTED_AT,
    }),
  });
  if (!response.ok) {
    const result = await response.json().catch(() => ({}));
    throw new Error(result.detail || 'lead_failed');
  }
  return response.json();
}
